import analytics from '@nerdwallet/analytics';
import { useQuery } from '@nerdwallet/apollo';
import appConfig from '@nerdwallet/app-config';
import { Box, Button, Link } from '@nerdwallet/currency';
import { FinishType } from '@nerdwallet/react-apply-now';
import Phone from '@nerdwallet/react-icon/gizmo/Phone';
import MarketplaceCta from '@nerdwallet/react-marketplace-cta';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import get from 'lodash/get';
import { usePathname, useSearchParams } from 'next/navigation';
import PropTypes from 'prop-types';
import { useDeviceType } from '~/app/lib/get-device-type';
import { getShoppingFinishedId } from '~/app/lib/get-shopping-finish-id';
import AppButtons from '../../app-buttons/app-buttons';

import { getButtonStyle } from '../../../lib/button-style-helper';
import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
  stringTextAreaSpec,
} from '../../../lib/editing-configs';
import {
  segmentImpressionId,
  trackInteraction,
  trackSegmentTrackEvent,
} from '../../../lib/segment-analytics';
import { baseSegmentAnalytics } from '../../../lib/utils';
import { Auth0Buttons } from '../../auth0-buttons/auth0-buttons';
import SegmentImpression from '../../segment-impression';
import UniversalText from '../../universal-text/universal-text';
import InsuranceButton, { insuranceButtonTypes } from './insurance-button';
import { QUERY } from './query';

export const CUSTOM_BUTTON_TYPE = 'custom';
export const PHONE_BUTTON_TYPE = 'phone';
const APP_STORE_BUTTON_TYPE = 'textMeApp';
const MARKETPLACE_APPLY_NOW = 'marketplace';
const ONBOARDING_GENERIC = 'onboardingGeneric';
const combinedSegmentAnalytics = {
  ...baseSegmentAnalytics,
  // eslint-disable-next-line no-underscore-dangle
  calling_deployable_name: appConfig?._meta?.deployable,
  // eslint-disable-next-line no-underscore-dangle
  calling_deployable_version: appConfig?._meta?.version,
};

const addCtaApplyNowLinkTracking = (marketplaceEntity, searchParams) => {
  if (marketplaceEntity?.cta?.link) {
    const updatedUrl = new URL(marketplaceEntity.cta?.link);
    Object.keys(searchParams).forEach((key) => {
      // Prevent to append undefined/null params
      if (searchParams[key]) {
        updatedUrl.searchParams.append(key, searchParams[key]);
      }
    });
    return {
      ...marketplaceEntity,
      cta: {
        ...marketplaceEntity.cta,
        link: updatedUrl.toString(),
      },
    };
  }
  return marketplaceEntity;
};

function buttonTypeInputConfig(fieldName, defaultItemValue = '') {
  const items = [
    {
      label: 'Custom URL',
      value: CUSTOM_BUTTON_TYPE,
    },
    {
      label: 'Dialog: Generic Onboarding',
      value: 'onboardingGeneric',
    },
    {
      label: 'App Download Buttons',
      value: APP_STORE_BUTTON_TYPE,
    },
    {
      label: 'Auto Insurance Entry Point',
      value: insuranceButtonTypes.AUTO_INSURANCE,
    },
    {
      label: 'Home Insurance Entry Point',
      value: insuranceButtonTypes.HOME_INSURANCE,
    },
    {
      label: 'Apply Now Button',
      value: MARKETPLACE_APPLY_NOW,
    },
    {
      label: 'Phone Number',
      value: PHONE_BUTTON_TYPE,
    },
  ];
  let sortedItems = [];
  if (defaultItemValue !== '') {
    sortedItems = items.sort((item) => (item.value === 'custom' ? -1 : 1));
  } else {
    sortedItems = items;
  }

  return stringSelectSpec(`${fieldName}.buttonType`, 'CTA', sortedItems);
}

const CtaButton = ({
  buttonClass,
  buttonPhoneNumber,
  showPhoneIcon,
  buttonType,
  buttonText,
  buttonURL,
  successURL,
  authDialogCopyOverrides,
  authDialogImageOverrides,
  appStoreURL,
  googlePlayURL,
  displayStyle,
  link,
  primary,
  disabled,
  loadingMessage,
  dataDependency,
  impressionId,
  buttonAnnotation,
  pricingCode,
  isInternal,
  defaultZipCode,
  trackClicksViaSegment,
  segment,
  disablePopback,
  captureCustomQueryParams,
  borderRadius,
  borderColor,
  borderWidth,
  includeShoppingFinishId,
}) => {
  const searchParams = useSearchParams();
  const deviceType = useDeviceType();

  const buttonUrlWithCustomQueryParams = () => {
    try {
      const composedUrl = new URL(buttonURL);
      // eslint-disable-next-line no-restricted-syntax
      for (const param of searchParams) {
        composedUrl.searchParams.append(param[0], param[1]);
      }
      return composedUrl;
    } catch (error) {
      return '';
    }
  };

  let buttonUrlToUse = buttonURL;
  if (captureCustomQueryParams) {
    buttonUrlToUse = buttonUrlWithCustomQueryParams();
  }

  let buttonTextToUse = buttonText;
  if (buttonType === PHONE_BUTTON_TYPE) {
    buttonUrlToUse = `tel:${buttonPhoneNumber?.replace(/\D/g, '')}`;

    if (!buttonTextToUse || buttonTextToUse === 'Button') {
      buttonTextToUse = buttonPhoneNumber;
    }

    buttonTextToUse = showPhoneIcon ? (
      <Box aria-label="Call us" display="flex" alignItems="center">
        <Phone size={16} style={{ marginRight: '8px' }} />
        {buttonTextToUse}
      </Box>
    ) : (
      <Box aria-label="Call us" display="flex" alignItems="center">
        {buttonTextToUse}
      </Box>
    );
  }

  const offerId = get(dataDependency, 'offerId', null);
  const { data, loading: fetching } = useQuery(QUERY, {
    skip: !offerId,
    variables: { id: offerId },
    fetchPolicy: 'no-cache',
  });
  const productType = data?.marketplaceEntityV2?.productType;
  const sectionName = `${productType}_article_offer`;
  const { pageViewId } = useAnalyticsPageView();
  const pathname = usePathname();

  const isInsuranceButtonType =
    buttonType === insuranceButtonTypes.AUTO_INSURANCE ||
    buttonType === insuranceButtonTypes.HOME_INSURANCE;

  const buttonStyle = getButtonStyle(primary);
  const displayBlock = displayStyle === 'block';

  if (isInsuranceButtonType) {
    return (
      <InsuranceButton
        type={buttonType}
        buttonText={buttonText}
        isInternal={isInternal}
        buttonAnnotation={buttonAnnotation}
        pricingCode={pricingCode}
        defaultZipCode={defaultZipCode || '94103'}
        disablePopback={disablePopback}
      />
    );
  }

  if (buttonType === MARKETPLACE_APPLY_NOW) {
    const marketplaceEntity = {
      ...data?.marketplaceEntityV2,
      cta: {
        ...data?.marketplaceEntityV2?.cta,
        text: buttonText || data?.marketplaceEntityV2?.cta?.text,
      },
      monetizable: 'YES_ASSUMED',
    };

    const shoppingFinishId = getShoppingFinishedId();

    const marketplaceButtonSegmentImpressionProps = {
      ...combinedSegmentAnalytics,
      impression_id: segmentImpressionId,
      sku_id: marketplaceEntity?.id,
      sku_level: marketplaceEntity?.entityType,
      sku_name: marketplaceEntity?.displayName,
      sku_product_type: marketplaceEntity?.productType,
      sku_source: 'marketplace',
      impression_page_url: pathname,
      impression_pageview_id: pageViewId,
      monetizable: 'YES_ASSUMED',
      sku_impression_id: segmentImpressionId,
      cta_type: 'apply_now_button',
      finish_type: FinishType.EXTERNAL_APPLICATION,
      device_type: deviceType,
      product_impression_id: segmentImpressionId,
      // eslint-disable-next-line no-underscore-dangle
      calling_deployable_name: appConfig?._meta?.deployable,
      // eslint-disable-next-line no-underscore-dangle
      calling_deployable_version: appConfig?._meta?.version,
      // eslint-disable-next-line no-underscore-dangle
      deployable_name: appConfig?._meta?.deployable,
      // eslint-disable-next-line no-underscore-dangle
      deployable_version: appConfig?._meta?.version,
      institution_id: marketplaceEntity?.institution?.id,
      institution_name: marketplaceEntity?.institution?.name,
      product_id: marketplaceEntity?.product?.id,
      product_name: marketplaceEntity?.product?.name,
      offer_id: marketplaceEntity?.id,
      offer_name: marketplaceEntity?.name,
      shopping_finish_id: includeShoppingFinishId
        ? shoppingFinishId
        : undefined,
    };

    const updatedMarketPlaceEntity = addCtaApplyNowLinkTracking(
      marketplaceEntity,
      marketplaceButtonSegmentImpressionProps
    );
    const handleMarketplaceCtaClick = () => {
      if (trackClicksViaSegment) {
        trackSegmentTrackEvent('Element Interaction', {
          entity_name: segment?.entityName || 'button',
          location: segment?.location || pathname,
          sub_location: segment?.subLocation || '',
          cta_type: 'apply_now_button',
          ...combinedSegmentAnalytics,
        });
      }
      analytics.track('element_interacted', {
        action: 'select',
        entity_name: sectionName,
        impression_id: impressionId,
        element_name: 'apply_now_button',
        interaction_type: 'press',
        cta_type: 'apply_now_button',
        device_type: deviceType,
        finish_type: FinishType.EXTERNAL_APPLICATION,
      });
    };

    return fetching ? (
      <p>Loading</p>
    ) : (
      <SegmentImpression
        eventName="MarketplaceCta Viewed"
        eventProps={marketplaceButtonSegmentImpressionProps}
      >
        <MarketplaceCta
          gqlMarketplaceEntity={updatedMarketPlaceEntity}
          analyticsPayloadOverrides={{
            product_impression_id: impressionId,
            section_name: sectionName,
            finish_type: FinishType.EXTERNAL_APPLICATION,
            cta_type: 'apply_now_button',
            baseSegmentAnalytics,
          }}
          onClick={handleMarketplaceCtaClick}
          omitImpression
        />
      </SegmentImpression>
    );
  }

  if (buttonType === APP_STORE_BUTTON_TYPE) {
    return (
      <AppButtons appStoreURL={appStoreURL} googlePlayURL={googlePlayURL} />
    );
  }

  if (buttonType === ONBOARDING_GENERIC) {
    const flowProps = {
      flowConfig: {},
    };

    if (successURL) {
      flowProps.onSuccess = successURL;
    }

    if (authDialogCopyOverrides) {
      flowProps.flowConfig.copyOverrides = {};
      authDialogCopyOverrides.forEach((copyOverride) => {
        flowProps.flowConfig.copyOverrides[copyOverride.copyKey] =
          copyOverride.copyValue;
      });
    }

    if (authDialogImageOverrides) {
      flowProps.flowConfig.imageOverrides = {};
      authDialogImageOverrides.forEach((imageOverride) => {
        flowProps.flowConfig.imageOverrides[imageOverride.imageKey] =
          imageOverride.imageValue;
      });
    }

    return (
      <Auth0Buttons.Container displayBlock={displayBlock}>
        <Auth0Buttons.GenericButton
          redirectTo={successURL}
          customCtaText={buttonText}
          buttonStyle={{ ...buttonStyle }}
          displayBlock={displayBlock}
          className={buttonClass}
        />
      </Auth0Buttons.Container>
    );
  }

  const customButtonSegmentImpressionProps = {
    impression_id: segmentImpressionId,
    sku_id: segment?.entityName,
    sku_level: segment?.entityName,
    sku_name: segment?.entityName,
    sku_product_type: 'cta_button',
    impression_page_url: pathname,
    impression_pageview_id: pageViewId,
    sku_impression_id: segmentImpressionId,
    finish_type: FinishType.EXTERNAL_APPLICATION,
    device_type: deviceType,
  };

  const handleCampaignLinkClick = () => {
    trackInteraction('Element Interaction', {
      ...baseSegmentAnalytics,
      entity_name: segment?.entityName || 'button',
      location: segment?.location || pathname,
      sub_location: segment?.subLocation || '',
    });
    analytics.track('element_interacted', {
      action: 'select',
      entity_name: segment?.entityName || 'button',
      impression_id: segmentImpressionId,
      interaction_type: 'press',
      device_type: deviceType,
      sub_location: segment?.subLocation || '',
    });
  };

  return (
    <SegmentImpression
      eventName="CtaButton Viewed"
      eventProps={customButtonSegmentImpressionProps}
    >
      {link ? (
        <Link
          style={{
            borderRadius,
            border: `${borderWidth}px solid ${borderColor}`,
          }}
          href={buttonUrlToUse}
          className={buttonClass}
          onClick={handleCampaignLinkClick}
        >
          {buttonTextToUse}
        </Link>
      ) : (
        <Button
          style={{
            borderRadius,
            border: `${borderWidth}px solid ${borderColor}`,
          }}
          onClick={handleCampaignLinkClick}
          className={buttonClass}
          href={buttonUrlToUse}
          block={displayBlock}
          disabled={disabled}
          loadingMessage={loadingMessage}
          {...buttonStyle}
        >
          {buttonTextToUse}
        </Button>
      )}
    </SegmentImpression>
  );
};

CtaButton.displayName = 'CTAButton';

CtaButton.propTypes = {
  buttonClass: PropTypes.string,
  buttonType: PropTypes.string,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  buttonURL: PropTypes.string,
  buttonPhoneNumber: PropTypes.string,
  showPhoneIcon: PropTypes.bool,
  successURL: PropTypes.string,
  disabled: PropTypes.bool,
  includeShoppingFinishId: PropTypes.bool,
  disablePopback: PropTypes.bool,
  loadingMessage: PropTypes.string,
  dataDependency: PropTypes.object,
  impressionId: PropTypes.string,
  authDialogCopyOverrides: PropTypes.arrayOf(
    PropTypes.shape({
      copyKey: PropTypes.string,
      copyValue: PropTypes.string,
    })
  ),
  authDialogImageOverrides: PropTypes.arrayOf(
    PropTypes.shape({
      imageKey: PropTypes.string,
      imageValue: PropTypes.string,
    })
  ),
  displayStyle: PropTypes.oneOf(['inline-block', 'block']),
  link: PropTypes.bool,
  primary: PropTypes.string,
  appStoreURL: PropTypes.string,
  googlePlayURL: PropTypes.string,
  buttonAnnotation: PropTypes.string,
  pricingCode: PropTypes.string,
  isInternal: PropTypes.bool,
  defaultZipCode: PropTypes.string,
  trackClicksViaSegment: PropTypes.bool,
  segment: PropTypes.shape({
    entityName: PropTypes.string,
    location: PropTypes.string,
    subLocation: PropTypes.string,
  }),
  captureCustomQueryParams: PropTypes.bool,
  enableQueryStringFieldValidation: PropTypes.bool,
  queryStringFieldValidationName: PropTypes.string,
  borderRadius: PropTypes.number,
  borderColor: PropTypes.string,
  borderWidth: PropTypes.number,
};

CtaButton.defaultProps = {
  segment: {},
  trackClicksViaSegment: false,
  buttonType: CUSTOM_BUTTON_TYPE,
  displayStyle: 'inline-block',
  link: false,
  primary: 'primary',
  disabled: false,
  loadingMessage: '',
  buttonAnnotation: '',
  pricingCode: '',
  isInternal: false,
  defaultZipCode: '94103',
  disablePopback: false,
  enableQueryStringFieldValidation: false,
  queryStringFieldValidationName: '',
  borderRadius: 0,
  borderColor: '#000',
  borderWidth: 0,
  includeShoppingFinishId: false,
};

CtaButton.getEditingConfig = (
  componentConfig,
  fieldName,
  allowedControlsList = []
) => {
  const buttonType = get(
    componentConfig,
    `props.${fieldName}.buttonType`,
    CUSTOM_BUTTON_TYPE
  );
  const isAuthDialog = buttonType === ONBOARDING_GENERIC;
  const isMarketplaceType = buttonType === 'marketplace';

  const marketPlaceInputConfig = isMarketplaceType
    ? [stringInputSpec(`${fieldName}.dataDependency.offerId`, 'Offer Id')]
    : [];

  const successURLSpec = isAuthDialog
    ? [
        stringInputSpec(`${fieldName}.successURL`, 'CTA on success url', {
          help: 'For Dialog CTAs, the URL to take the user after they have successfully registered',
        }),
      ]
    : [];

  const appStoreUrlOverridesSpec =
    buttonType === APP_STORE_BUTTON_TYPE ? AppButtons.getEditingConfig() : [];

  const shouldOutputCustomButtonValues = get(
    componentConfig,
    `props.${fieldName}.customButtonColorValues`,
    false
  );

  const displayStyleEditorControls = allowedControlsList.includes(
    'displayStyle'
  )
    ? [
        stringSelectSpec(
          `${fieldName}.displayStyle`,
          'Display',
          [
            { label: 'Inline', value: 'inline-block' },
            { label: 'Block', value: 'block' },
          ],
          {
            help: 'Defaults to "Inline"',
          }
        ),
      ]
    : [];

  const loadingEditorControls = allowedControlsList.includes('loading')
    ? [boolCheckboxSpec(`${fieldName}.loading`, 'Loading?')]
    : [];

  const disabledEditorControls = allowedControlsList.includes('disabled')
    ? [boolCheckboxSpec(`${fieldName}.disabled`, 'Disabled?')]
    : [];

  const loadingMessageEditorControls = allowedControlsList.includes(
    'loadingMessage'
  )
    ? [stringInputSpec(`${fieldName}.loadingMessage`, 'Loading Message?')]
    : [];

  const alignmentEditorControls = allowedControlsList.includes('alignment')
    ? [
        stringSelectSpec(
          `${fieldName}.alignment`,
          'Alignment',
          [
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ],
          {
            help: 'Defaults to "Left"',
          }
        ),
      ]
    : [];

  const queryStringFieldValidationFieldInputConfig = get(
    componentConfig,
    `props.${fieldName}.enableQueryStringFieldValidation`,
    false
  )
    ? [
        stringInputSpec(
          `${fieldName}.queryStringFieldValidationName`,
          'Query String FieldName',
          {
            help: 'The query string field name to validate',
          }
        ),
      ]
    : [];

  const outputButtonPhoneNumberInput =
    buttonType === PHONE_BUTTON_TYPE
      ? [
          stringInputSpec(`${fieldName}.buttonPhoneNumber`, 'Phone Number', {
            help: 'Phone number to call when the button is clicked',
          }),
          boolCheckboxSpec(`${fieldName}.showPhoneIcon`, 'Show Phone Icon?', {
            help: 'If enabled, a phone icon will be shown next to the button text',
          }),
        ]
      : [];

  const outputButtonTargetUrlInput =
    buttonType === CUSTOM_BUTTON_TYPE
      ? [
          stringInputSpec(
            `${fieldName}.buttonURL`,
            'Custom button target url',
            {
              help: 'URL for the user to be redirected to on click.',
            }
          ),
          boolCheckboxSpec(
            `${fieldName}.enableQueryStringFieldValidation`,
            'Enable QueryString Field Validation?',
            {
              help: 'If enabled, the button will only be enabled if the query string field is present in the URL',
            }
          ),
          ...queryStringFieldValidationFieldInputConfig,
        ]
      : [];

  const insuranceInputConfig =
    buttonType === insuranceButtonTypes.AUTO_INSURANCE ||
    buttonType === insuranceButtonTypes.HOME_INSURANCE
      ? [
          stringInputSpec(
            `${fieldName}.buttonAnnotation`,
            'CTA Button Annotation'
          ),
          stringInputSpec(`${fieldName}.pricingCode`, 'CTA Pricing Code'),
          stringInputSpec(
            `${fieldName}.defaultZipCode`,
            'CTA Default Zip Code'
          ),
          boolCheckboxSpec(
            `${fieldName}.isInternal`,
            'Use NW Internal Experience?'
          ),
          boolCheckboxSpec(`${fieldName}.disablePopback`, 'Disable Pop-back?'),
        ]
      : [];

  const customButtonColorValuesControls = shouldOutputCustomButtonValues
    ? [
        stringInputSpec(
          `${fieldName}.customButtonActiveColor`,
          'Custom Button Active Color'
        ),
        stringInputSpec(
          `${fieldName}.customButtonHoverColor`,
          'Custom Button Hover Color'
        ),
        stringInputSpec(
          `${fieldName}.customButtonTextActiveColor`,
          'Custom Button Text Active Color'
        ),
        stringInputSpec(
          `${fieldName}.customButtonTextHoverColor`,
          'Custom Button Text Hover Color'
        ),
      ]
    : [];

  const borderRadiusEditorControls = [
    numberInputSpec(`${fieldName}.borderRadius`, 'Border Radius'),
  ];
  const borderColorEditorControls = [
    stringInputSpec(`${fieldName}.borderColor`, 'Border Color'),
    numberInputSpec(`${fieldName}.borderWidth`, 'Border Width'),
  ];

  const outputButtonPropertiesInputConfig =
    buttonType === CUSTOM_BUTTON_TYPE
      ? [
          group('Properties', [
            stringSelectSpec(
              `${fieldName}.primary`,
              'Button Style',
              [
                { label: 'Default', value: '' },
                { label: 'Primary', value: 'primary' },
                { label: 'Secondary', value: 'secondary' },
                { label: 'Tertiary', value: 'tertiary' },
              ],
              {
                help: 'Primary output a GREEN button with WHITE text; Secondary output a WHITE button with GREEN text, Tertiary output a WHITE button without border',
              }
            ),
            boolCheckboxSpec(
              `${fieldName}.customButtonColorValues`,
              'Add custom button color values?'
            ),
            ...customButtonColorValuesControls,
            ...disabledEditorControls,
            ...loadingEditorControls,
            ...loadingMessageEditorControls,
            ...displayStyleEditorControls,
            ...alignmentEditorControls,
            ...borderRadiusEditorControls,
            ...borderColorEditorControls,
          ]),
        ]
      : [];

  const trackClicksViaSegmentInputConfig = get(
    componentConfig,
    `props.${fieldName}.trackClicksViaSegment`,
    false
  )
    ? [
        stringInputSpec(`${fieldName}.segment.entityName`, 'Entity Name'),
        stringInputSpec(`${fieldName}.segment.location`, 'Location'),
        stringInputSpec(`${fieldName}.segment.subLocation`, 'Sub Location'),
      ]
    : [];

  return [
    buttonTypeInputConfig(fieldName, buttonType ? '' : CUSTOM_BUTTON_TYPE),
    stringTextAreaSpec(`${fieldName}.buttonText`, 'CTA Text'),
    ...successURLSpec,
    ...appStoreUrlOverridesSpec,
    ...outputButtonPhoneNumberInput,
    ...outputButtonTargetUrlInput,
    ...marketPlaceInputConfig,
    ...insuranceInputConfig,
    ...outputButtonPropertiesInputConfig,

    ...UniversalText.getEditingConfig(
      componentConfig,
      'Extra Text',
      [
        'textSize',
        'textType',
        'fontWeight',
        'padding',
        'margin',
        'groupButton',
        'fontFamily',
        'textColor',
      ],
      `${fieldName}.ctaExtraText`,
      { text: { textType: 'text' } }
    ),

    group('Tracking', [
      boolCheckboxSpec(
        `${fieldName}.trackClicksViaSegment`,
        'Track clicks via Segment?'
      ),
      boolCheckboxSpec(
        `${fieldName}.includeShoppingFinishId`,
        'Include Shopping Finish Id'
      ),
      ...trackClicksViaSegmentInputConfig,
      stringInputSpec(`${fieldName}.componentName`, 'Component Name', {
        help: 'For Tracking Purposes',
      }),
      repeatable(
        `${fieldName}.buttonParams`,
        'Tracking Params',
        'Param',
        () => [
          stringInputSpec(`queryKey`, 'Key'),
          stringTextAreaSpec(`queryValue`, 'Value'),
        ]
      ),
      boolCheckboxSpec(
        `${fieldName}.captureCustomQueryParams`,
        'Capture Custom Query Params?'
      ),
    ]),
  ];
};

export default CtaButton;
